import * as React from 'react';
import { css } from '@emotion/css';
import {
  Button,
  Card,
  Divider,
  Empty,
  Image,
  List,
  notification,
  Popover,
  Tooltip,
  Typography,
} from 'antd';
import { groupBy, mapValues } from 'lodash';
import { getSearchFavorites } from '../../../api/apis';
import Loader from '../../shared/Loader';
import { getFormattedDate } from '../../../utils/helper';
import { ISearchFavoritesConfigInterface } from '../../../utils/constants';
import Meta from 'antd/lib/card/Meta';

export interface ISearchFavoritesProps
  extends ISearchFavoritesConfigInterface {}
const searchFavoritesContainer = css`
  .search-favorite-wrapper {
    background: #2696ff;
    padding: 1rem;
    text-align: center;
  }

  .favorite-items-lists {
    .favorite-search-card {
      position: relative;

      .ant-card-meta-detail {
        display: flex;
        flex-direction: column;
        .ant-card-meta-title {
          margin-bottom: 0;
        }
      }

      .ant-card-meta-avatar {
        img.--item-image {
          height: 75px;
          width: auto;
        }
      }

      .--view-source {
        position: absolute;
        right: 10px;
        bottom: 10px;
      }
    }
  }
`;

type searchFavoritesItemType = Array<Record<string | number | symbol, any>>;

export default function SearchFavorites(_props: ISearchFavoritesProps) {
  const { displayFields, showRawDocument = true, rawDocumentCTA } = _props;
  const [data, setData] = React.useState<searchFavoritesItemType>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  const renderLists = () => {
    const groupedLists = mapValues(groupBy(data, 'timestamp'));

    const groupedListsHeaders = Object.keys(groupedLists);

    if (groupedListsHeaders.length === 0) {
      return (
        <Empty
          description={
            !loading ? (
              <span>We couldn't find any search favorites!</span>
            ) : (
              <span>Fetching...</span>
            )
          }
        />
      );
    }

    return groupedListsHeaders.map((headerKey, index) => {
      let dataSource: searchFavoritesItemType = [];
      dataSource = groupedLists[headerKey].map(item => ({
        source: item.source,
        ...displayFields(item),
      }));
      if (dataSource.length === 0) return null;

      const renderSourcePopover = (source: Object) => {
        if (!showRawDocument) {
          return null;
        }

        return (
          <Popover
            autoAdjustOverflow
            content={<pre>{JSON.stringify(source, null, 2)}</pre>}
            title="Source"
            showArrow={false}
            placement="bottomLeft"
            overlayInnerStyle={{
              overflowX: 'auto',
              maxWidth: '300px',
            }}
          >
            <div className="--view-source">
              {rawDocumentCTA ? (
                rawDocumentCTA
              ) : (
                <Button type="link">View Source</Button>
              )}
            </div>
          </Popover>
        );
      };

      return (
        <List
          key={headerKey}
          header={
            <Divider style={{ margin: 0 }} plain>
              <b>{headerKey}</b>
            </Divider>
          }
          bordered
          dataSource={dataSource}
          renderItem={({ description, title, image, source }) => (
            <Card hoverable className="favorite-search-card">
              <Meta
                style={{ width: ' 100%' }}
                avatar={
                  <Image
                    src={
                      image ??
                      'data:image/png;base64,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'
                    }
                    alt={`image for ${title}`}
                    className="--item-image"
                  />
                }
                title={
                  <Tooltip
                    title={title}
                    showArrow={false}
                    overlayInnerStyle={{
                      maxHeight: '70vh',
                      overflowY: 'auto',
                      width: '300px',
                    }}
                  >
                    <Typography.Title
                      ellipsis={{ rows: 1, expandable: false }}
                      level={5}
                    >
                      {title}
                    </Typography.Title>
                  </Tooltip>
                }
                description={
                  <Tooltip
                    title={description}
                    showArrow={false}
                    overlayInnerStyle={{
                      maxHeight: '70vh',
                      overflowY: 'auto',
                      width: '300px',
                    }}
                  >
                    <Typography.Paragraph
                      ellipsis={{ rows: 3, expandable: false }}
                    >
                      {description}
                    </Typography.Paragraph>
                  </Tooltip>
                }
              />
              {renderSourcePopover(source)}
            </Card>
          )}
          footer={
            index === groupedListsHeaders.length - 1 ? (
              <Divider plain>That's all folks! 🤐</Divider>
            ) : null
          }
        />
      );
    });
  };
  const fetchSearchFavorites = async () => {
    setLoading(true);

    try {
      const favoriteSearches = await getSearchFavorites();
      setData(
        (favoriteSearches.length ? favoriteSearches : []).map((item: any) => ({
          ...item,
          timestamp: getFormattedDate(item.timestamp, {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
        }))
      );
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Error fetching the search favorites!',
      });
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    fetchSearchFavorites();
  }, []);

  return (
    <div className={searchFavoritesContainer}>
      <Loader loading={loading}>
        <div className="favorite-items-lists">{renderLists()}</div>
      </Loader>
    </div>
  );
}
